import { Link } from "react-router-dom";
import { Instagram, Youtube, Linkedin, Music, Music2 } from "lucide-react";
import Container from "@/components/editorial/Container";
import NewsletterForm from "@/components/editorial/NewsletterForm";
import SponsorStrip from "@/components/editorial/SponsorStrip";
import { FOOTER_NAV } from "@/data/content";

const ICONS = { Instagram, Youtube, Linkedin, Music, Music2 };

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-charcoal text-cream/75">
      <Container className="pt-20 md:pt-24 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
          {/* Brand + mini newsletter */}
          <div className="md:col-span-5">
            <Link to="/" className="inline-flex items-baseline">
              <span className="font-serif italic text-2xl text-cream">Mujeres</span>
              <span className="mx-1 text-terracotta font-serif text-3xl leading-none">·</span>
              <span className="font-serif text-2xl text-cream">Motor</span>
            </Link>
            <p className="font-serif italic text-cream/70 text-lg mt-4 max-w-sm">
              Historias reales. Comunidad viva. Impacto visible.
            </p>
            <div className="mt-8 max-w-md">
              <p className="text-[11px] uppercase tracking-[0.22em] text-cream/50 mb-3">
                Newsletter
              </p>
              <NewsletterForm
                variant="inline"
                tone="dark"
                testidPrefix="footer-nl"
              />
            </div>
          </div>

          {/* Columns */}
          <div className="md:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-8">
            {Object.entries(FOOTER_NAV).map(([heading, links]) => (
              <div key={heading}>
                <p className="text-[11px] uppercase tracking-[0.22em] text-gold mb-5">
                  {heading}
                </p>
                <ul className="space-y-3">
                  {links.map((l) => {
                    const Icon = l.icon ? ICONS[l.icon] : null;
                    const isExternal = l.href.startsWith("#") || l.href.startsWith("http");
                    const content = (
                      <span className="inline-flex items-center gap-2 text-sm text-cream/80 hover:text-cream transition-colors duration-300">
                        {Icon && <Icon className="h-4 w-4" />}
                        {l.label}
                      </span>
                    );
                    return (
                      <li key={l.label}>
                        {isExternal ? (
                          <a
                            href={l.href}
                            target={l.href.startsWith("http") ? "_blank" : undefined}
                            rel={l.href.startsWith("http") ? "noopener noreferrer" : undefined}
                            onClick={() => {
                              if (l.href.startsWith("http")) {
                                import("@/lib/analytics").then(({ trackOutbound }) =>
                                  trackOutbound(l.href, { surface: "footer", label: l.label })
                                );
                              }
                            }}
                          >
                            {content}
                          </a>
                        ) : (
                          <Link to={l.href}>{content}</Link>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-14 pt-10 border-t border-cream/10">
          <SponsorStrip
            tier="founding"
            eyebrow="Founding Partners 2026"
            tone="dark"
            size="md"
            centered
            cta="Conviértete en Founding Partner"
            ctaHref="/for-brands#tiers"
            testid="footer-founding-strip"
          />
        </div>

        {/* Content / Media Partners · smaller "Aliados" strip */}
        <div className="mt-10 pt-8 border-t border-cream/5" data-testid="footer-content-wrapper">
          <SponsorStrip
            tier="content"
            eyebrow="Aliados editoriales"
            tone="dark"
            size="sm"
            centered
            testid="footer-content-strip"
          />
        </div>

        <div className="mt-10 pt-6 border-t border-cream/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-cream/50">
          <p>© {new Date().getFullYear()} Mujeres Motor · Una producción de Baklight</p>
          <div className="flex gap-6">
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-cream">Aviso de privacidad</Link>
            <a href="#" className="hover:text-cream">Términos</a>
          </div>
        </div>
      </Container>
    </footer>
  );
}
